import { useEffect, useRef, useState } from 'react'
import type { AxiosError } from 'axios'
import { Centrifuge } from 'centrifuge'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '../../lib/react-query'
import { chatApi, ChatStreamEvent } from '../../services/api/chat.api'
import { useAuthStore } from '../../store/authStore'

export interface StreamingToolCall {
  id: string
  name: string
  status: string
  summary?: string
  /**
   * The call's arguments, already an OBJECT on the wire — never `JSON.parse` it.
   * Kept so an expanded step can show what the agent actually ran, which on a
   * security tool is most of the point of showing the call at all.
   */
  args?: Record<string, unknown>
}

export interface PendingApproval {
  toolCallId: string
  toolName: string
  args: Record<string, unknown>
}

export interface StreamingTurn {
  turnId: string
  text: string
  tools: StreamingToolCall[]
  thinking?: string
  /** The turn ended on `done`. */
  done: boolean
  /**
   * The turn ended on `approval_required` — halted, waiting for a human. It has
   * neither finished nor failed, so treating "not done and not error" as "still
   * streaming" would spin the typing indicator forever. This is the third
   * terminal state.
   *
   * Cleared when the resumed turn begins. Approving resumes on a NEW `turn_id`
   * whose first event is a `tool_call`, not a `started` (§12 step 5, §18:24),
   * so `turnBase` — not the `started` case — is what ends this state.
   */
  awaitingApproval: boolean
  pendingApprovals: PendingApproval[]
  /**
   * Client clocks, not server timestamps: they measure when WE observed the
   * turn open and close. That is right for a live turn and meaningless for one
   * replayed out of backfill, whose whole history folds through in a single
   * batch — so consumers must suppress implausibly short measurements rather
   * than trust these blindly. `traceFromTurn` is where that rule lives.
   */
  startedAt: number
  endedAt?: number
}

/**
 * The turn `evt` should fold into: `prev` when it belongs to the same turn, a
 * blank turn when it does not.
 *
 * This is the fold's turn-boundary rule, and it exists in one place on purpose.
 * Every case used to write `prev ?? {…fresh…}`, which starts a turn only when
 * there is no turn at all — so an event belonging to a *different* turn merged
 * into whatever was already in state, leaving `turnId` pointing at the dead
 * turn and the resumed answer concatenated onto the halted turn's text.
 *
 * Keying on `turn_id` rather than on any particular event type is what makes
 * this robust to how a turn actually begins. `since=0` replays the last 500
 * events oldest-first, so a truncated history can open a turn at `thinking` or
 * `tool_call` just as easily as at `started` — and `started` itself is NOT
 * reliably first: an approval resume emits one MID-TURN, under the same
 * `turn_id`, as the continuation of work already on screen. The vendored
 * migration contract claimed the resume opened a NEW turn (§12 step 5,
 * §12:762); it does not, and the code written around that claim dropped the
 * approved tool from the timeline. See `resumed` below and
 * docs/superpowers/specs/2026-08-11-chat-stream-fixes-backend.md, change 4.
 *
 * Both shapes are handled: a differing `turn_id` opens a fresh turn here, and a
 * matching one is carried through `resumed` instead.
 */
function turnBase(prev: StreamingTurn | null, evt: ChatStreamEvent): StreamingTurn {
  if (prev && prev.turnId === evt.turn_id) return prev
  return {
    turnId: evt.turn_id,
    text: '',
    tools: [],
    done: false,
    awaitingApproval: false,
    pendingApprovals: [],
    startedAt: Date.now(),
  }
}

/**
 * A halted turn has started producing again.
 *
 * The vendored contract said an approval resume carries a NEW `turn_id`, so the
 * only thing that ever cleared `awaitingApproval` was `turnBase` opening a fresh
 * turn. The service actually resumes on the SAME turn, which means `turnBase`
 * returns `prev` untouched and the halt would latch for good:
 * `isStreamTerminal` is `done || awaitingApproval`, so the turn reads as
 * finished, the loading cue never returns while the approved tool runs, and the
 * already-decided card stays on screen beside it.
 *
 * Applied by every NON-terminal case, so any sign of life ends the halt
 * regardless of which event type the resume happens to lead with — §12 emits
 * `tool_call`/`tool_result` only for EXECUTED calls, so an all-denied resume
 * leads with something else entirely.
 *
 * Dropping the live approval card here is intended: the persisted
 * `approval_request` block is authoritative, and `approval_required`
 * invalidated the message list precisely so it is on hand.
 *
 * Scoped to `awaitingApproval` rather than applied unconditionally, so a late
 * event on a turn that reached `done` cannot un-finish it.
 */
function resumed(turn: StreamingTurn): StreamingTurn {
  if (!turn.awaitingApproval) return turn
  // `endedAt` goes too: the halt stamped it, and the turn is plainly not over.
  // Left in place, the trace would report a duration ending at the pause.
  return { ...turn, awaitingApproval: false, pendingApprovals: [], endedAt: undefined }
}

/**
 * How often the degraded path re-reads `GET /chat/{id}/events?since=` when the
 * socket could not be opened at all (§14). It is a read of a cursor-paged log,
 * not a fetch of the whole conversation, so the cost of each poll is bounded by
 * what has actually happened since the last one — usually nothing.
 *
 * Chosen to sit under the time an answer takes to be worth reading: any longer
 * and the fallback reads as "the assistant is slow" rather than as the socket
 * being absent, which is the impression §14 is trying to avoid.
 */
const POLL_INTERVAL_MS = 3000

type ChatStreamConnectionState = 'connecting' | 'connected' | 'disconnected'

interface UseChatStreamResult {
  streaming: StreamingTurn | null
  connectionState: ChatStreamConnectionState
  error: string | null
}

/**
 * Subscribes to a chat's Centrifugo channel (as returned by the stream
 * token) and folds the
 * streamed turn events (`started`/`thinking`/`tool_call`/`tool_result`/
 * `answer`/`done`/`error`) into a single in-progress `StreamingTurn`.
 *
 * `approval_required` is folded here too: it is the third TERMINAL state (the
 * turn is halted waiting on a human), so it sets `awaitingApproval` rather
 * than `done`.
 *
 * Modeled on `useNodeEvents.ts`, but note the dedup identity differs: node
 * events carry an `event_id`, chat events do not, so live and backfilled
 * events are deduped by `${turn_id}:${seq}` instead. On every `connected`
 * (initial connect *and* reconnect) we replay `chatApi.getEvents({ since })`
 * through the same fold so a dropped-then-reconnected client catches back up.
 * On any terminal event the persisted message is authoritative, so we
 * invalidate the chat's messages query rather than trying to keep the local
 * streaming buffer in sync.
 *
 * NOTE FOR CONSUMERS: the first connect replays from `since: 0`, and the new
 * service retains the last 500 events per chat with NO TTL (Django's ring also
 * expired after an hour, so backfill used to come back empty for any chat left
 * overnight). Reopening a chat therefore replays its last completed turn, and
 * `streaming` can be non-null with `done: true` on the very first render.
 * "Streaming is non-null" does NOT mean "a turn is live" — check the terminal
 * flags before rendering it as an in-flight bubble.
 */
export function useChatStream(chatId: string | null): UseChatStreamResult {
  const orgId = useAuthStore((state) => state.orgId)
  const queryClient = useQueryClient()

  const [streaming, setStreaming] = useState<StreamingTurn | null>(null)
  const [connectionState, setConnectionState] = useState<ChatStreamConnectionState>('connecting')
  const [error, setError] = useState<string | null>(null)

  // `seq` is monotonic for the life of the CHAT (a per-channel counter), not
  // per turn — so `seq` alone is already unique and `${turn_id}:${seq}` is
  // merely redundant, not wrong. Keep it: it stays correct and reads clearly.
  //
  // `seq` is ALSO the `since` cursor for `getEvents` (§11), which is why the
  // ref below tracks it rather than the Centrifugo publication `offset`. Those
  // are two different counters — `offset` is Centrifugo's per-channel
  // publication index, `seq` is the service's — and `next_since` from backfill
  // is a `seq`. Advancing the same ref from both, as this once did, mixed the
  // two number spaces under a `Math.max`, so name it for the one it holds.
  const seenIds = useRef<Set<string>>(new Set())
  const lastSeq = useRef<number>(0)
  // The turn the last handled event belonged to, so a turn boundary can be
  // detected once, before the switch, rather than inside a state updater.
  const currentTurnId = useRef<string | null>(null)

  useEffect(() => {
    setStreaming(null)
    setError(null)

    if (!chatId) {
      setConnectionState('disconnected')
      return
    }
    // Narrow once so the closures below don't need non-null assertions.
    const cid: string = chatId

    seenIds.current = new Set()
    lastSeq.current = 0
    currentTurnId.current = null
    setConnectionState('connecting')

    let active = true
    let cent: Centrifuge | null = null
    // Declared inside the effect so it is per-`chatId`: the teardown below runs
    // on every dep change, and `ChatInner` is never unmounted on a chat switch
    // (`DrawerMessenger` renders it unconditionally), so a timer hoisted to a
    // ref would poll the previous chat for the rest of the session.
    let pollTimer: ReturnType<typeof setInterval> | null = null

    // Every event carries `seq`, live or backfilled, so one path advances the
    // cursor for both.
    function handle(evt: ChatStreamEvent) {
      if (!active) return
      const key = `${evt.turn_id}:${evt.seq}`
      if (seenIds.current.has(key)) return
      seenIds.current.add(key)
      lastSeq.current = Math.max(lastSeq.current, evt.seq)

      // A new turn always supersedes a stale error — a prior turn's `error`
      // event, or one replayed from backfill history on remount.
      //
      // Detected here, once, rather than per-case, because `turnBase` lets a
      // turn begin at ANY event type: `since=0` replays the last 500 events
      // oldest-first, so a truncated history can open a turn at `thinking` or
      // `tool_call` just as easily as at `started`. Tying the reset to those
      // two cases alone would leave the previous turn's error banner up.
      //
      // A ref, not a `setStreaming` updater: updaters must be pure and React
      // may replay them. (StrictMode is commented out at src/index.tsx:78, so
      // that is dormant rather than live — but `useNodeEvents`, the hook this
      // one was modelled on, has already been bitten by exactly this class of
      // impurity, and one instance of it in the pair is enough.)
      const isNewTurn = currentTurnId.current !== evt.turn_id
      currentTurnId.current = evt.turn_id
      if (isNewTurn) setError(null)

      switch (evt.type) {
        // NOT a hard assignment. `started` arrives mid-turn as the continuation
        // of an approval resume, under the same `turn_id`, and replacing state
        // wholesale here threw away the tool the user had just approved. A
        // genuinely new turn still gets a clean slate — `turnBase` gives it one
        // off the changed `turn_id`.
        case 'started':
          setStreaming((prev) => resumed(turnBase(prev, evt)))
          break

        case 'answer':
          setStreaming((prev) => {
            const base = resumed(turnBase(prev, evt))
            return { ...base, text: base.text + (evt.delta ?? '') }
          })
          break

        case 'thinking':
          setStreaming((prev) => {
            const base = resumed(turnBase(prev, evt))
            return { ...base, thinking: (base.thinking ?? '') + (evt.delta ?? '') }
          })
          break

        case 'tool_call':
          setStreaming((prev) => {
            const base = resumed(turnBase(prev, evt))
            return {
              ...base,
              tools: [
                ...base.tools,
                {
                  id: evt.id ?? '',
                  name: evt.name ?? '',
                  status: 'running',
                  args: evt.args as Record<string, unknown> | undefined,
                },
              ],
            }
          })
          break

        case 'tool_result':
          setStreaming((prev) => {
            // Unlike the cases above, this one never *starts* a turn — hence
            // no `turnBase`. A result has nothing to update when there is no
            // turn, and equally when the turn on screen is a different one:
            // the tool it refers to is not in `prev.tools`, so opening a blank
            // turn here would only produce one that can never be populated.
            // Reachable on the all-denied approval path, where the resumed
            // turn has no `tool_call` to lead with — a denial is fed back as
            // the tool result itself (§12).
            if (!prev || prev.turnId !== evt.turn_id) return prev
            const base = resumed(prev)
            return {
              ...base,
              tools: base.tools.map((tool) =>
                tool.id === evt.id
                  ? { ...tool, status: evt.status ?? tool.status, summary: evt.summary }
                  : tool,
              ),
            }
          })
          break

        case 'approval_required': {
          // The new service sends every gated call in `evt.calls[]`, with
          // `args` already an OBJECT. Do not look for a flat `tool_call_id`
          // (it never exists — the handler would break out and silently drop
          // every card) and do not JSON.parse `args` (it would throw, get
          // caught, and render a card with no visible arguments).
          //
          // Key names differ from the persisted block: event `id`/`name` are
          // the block's `tool_call_id`/`tool_name`. Normalised here so the
          // card sees one shape either way.
          // No cast on `args`: `StreamApprovalCall.args` is already
          // `Record<string, unknown>`, so one would be a no-op today and would
          // silently absorb a future retyping to `unknown` — the very drift
          // this task exists to catch. The `?? {}` is a runtime fallback for a
          // call that omits the field.
          const approvals: PendingApproval[] = (evt.calls ?? []).map((call) => ({
            toolCallId: call.id,
            toolName: call.name,
            args: call.args ?? {},
          }))
          if (approvals.length === 0) break

          setStreaming((prev) => {
            const base = turnBase(prev, evt)
            return {
              ...base,
              // Terminal: the turn is halted pending a human decision.
              awaitingApproval: true,
              pendingApprovals: [...base.pendingApprovals, ...approvals],
              endedAt: Date.now(),
            }
          })
          // The assistant message and its approval_request block are persisted
          // in one transaction before this event fires, so refetching gives us
          // a card that survives reload. Stream state alone is not enough.
          queryClient.invalidateQueries({ queryKey: queryKeys.chat.messages(orgId, cid) })
          break
        }

        case 'done':
          setStreaming((prev) => {
            // `done` now carries `content`, the full answer text (Django sent
            // `message_id` and no text). The accumulated `answer` deltas
            // normally already equal it, but they can be short a chunk when a
            // reconnect lands past the 500-event backfill ring — and the live
            // bubble stays on screen until the refetch below resolves, so a
            // truncated answer is visible in the meantime. Adopting `content`
            // self-heals that window.
            //
            // Guarded rather than `??`: a terminal event must never *erase*
            // text the deltas already produced, so an absent or empty
            // `content` leaves the accumulation alone.
            const text = evt.content && evt.content.length > 0 ? evt.content : undefined
            const base = turnBase(prev, evt)
            return { ...base, text: text ?? base.text, done: true, endedAt: Date.now() }
          })
          queryClient.invalidateQueries({ queryKey: queryKeys.chat.messages(orgId, cid) })
          break

        case 'error':
          setError(evt.detail ?? 'Unknown streaming error')
          break

        default:
          break
      }
    }

    async function backfill() {
      try {
        const resp = await chatApi.getEvents(cid, { since: lastSeq.current })
        for (const evt of resp.events) handle(evt)
        // `next_since` is a `seq`, the same counter `handle` advances — live
        // events may have pushed the cursor past it while this request was in
        // flight, so never move it backward.
        lastSeq.current = Math.max(lastSeq.current, resp.next_since)
      } catch {
        // Backfill failure is non-fatal — live events keep flowing; the next
        // reconnect will retry the catch-up read.
      }
    }

    /**
     * The degraded path: read `/events` on a loop because no socket will ever
     * deliver them.
     *
     * `backfill()` is reused verbatim rather than given a polling twin — it
     * already folds through `handle()` (so dedup by `${turn_id}:${seq}` and the
     * terminal-event message invalidation both apply unchanged) and already
     * advances `lastSeq`, which is exactly the cursor a poll needs. Each tick
     * therefore asks only for what has happened since the last one.
     *
     * Fires once immediately: waiting a whole interval before the first look
     * would put a silent gap in front of an answer that may already be
     * persisted.
     */
    function startPolling() {
      if (pollTimer) return
      void backfill()
      pollTimer = setInterval(() => {
        void backfill()
      }, POLL_INTERVAL_MS)
    }

    async function connect() {
      try {
        const { token, url, channel } = await chatApi.centrifugoToken(cid)
        if (!active) return

        cent = new Centrifuge(url, {
          token,
          getToken: async () => {
            const fresh = await chatApi.centrifugoToken(cid)
            return fresh.token
          },
        })

        cent.on('connected', () => {
          if (!active) return
          setConnectionState('connected')
          void backfill()
        })
        cent.on('disconnected', () => {
          if (!active) return
          setConnectionState('disconnected')
        })

        // The stream token carries a `channels` claim, so Centrifugo
        // server-side-subscribes this connection to `channel` on connect.
        // Live events therefore arrive as CLIENT-level publications — we must
        // NOT create a client-side newSubscription for the same channel, which
        // would fail with "already subscribed" (code 105) and receive nothing.
        // Filter on the exact channel the backend scoped the token to; a
        // locally reconstructed `chat:{id}` name is NOT guaranteed to match
        // the server's (namespaced) channel and would drop every event.
        //
        // The envelope's `ctx.offset` is deliberately ignored: it is
        // Centrifugo's own publication index, a different counter from the
        // service's `seq`, and `seq` is what `getEvents({since})` takes.
        cent.on('publication', (ctx: { channel: string; data: unknown }) => {
          if (ctx.channel === channel) handle(ctx.data as ChatStreamEvent)
        })

        cent.connect()
      } catch (err) {
        if (!active) return
        setConnectionState('disconnected')
        /**
         * §14: a 503 here means realtime is not configured — an operator
         * problem, not a failed turn — and "the answer still arrives". So it
         * gets no `error`: that value is what `useChatTurn` turns into
         * `<ChatError/>`, and an error banner over a conversation that is about
         * to answer is simply wrong. Every other status still surfaces; the
         * user has no other way to learn the socket never opened.
         */
        const status = (err as AxiosError | undefined)?.response?.status
        if (status !== 503) {
          setError(err instanceof Error ? err.message : 'Failed to connect to chat stream')
        }
        /**
         * Polling, however, is unconditional. `backfill()` was only ever
         * reachable from `cent.on('connected')`, and a token failure means
         * there is no `cent` — so without this, whatever the status, no
         * `/events` read ever happens and the answer never reaches the client.
         * The 503 is merely the case the contract names.
         */
        startPolling()
      }
    }

    void connect()

    return () => {
      active = false
      if (pollTimer) clearInterval(pollTimer)
      pollTimer = null
      if (cent) cent.disconnect()
      cent = null
    }
  }, [chatId, orgId, queryClient])

  return { streaming, connectionState, error }
}
